/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Mar 12 2024
 **/

import { useAtom } from "jotai";

import { useEventHandler } from "@rfice/eventhandler";

import { CreateSpaceGroupIDAtom } from "./atom";
import { useCreateSpaceState, useCreateSpaceActions, useSidebarActions } from "@/frameworks/core/hooks";
import { EventID, SidebarID } from "@/frameworks/core/commons";

type CreateSpaceStateType = WebServices.Space.Create.Request &
	Partial<WebServices.SpaceTheme.GetRandom.Data> & { [key: string]: unknown };

export default function CreateSpaceProvider() {
	const { state } = useCreateSpaceState();
	const createSpaceActions = useCreateSpaceActions();
	const [groupID, setGroupID] = useAtom(CreateSpaceGroupIDAtom);
	const sidebarActions = useSidebarActions();

	useEventHandler(
		EventID.OPEN_CREATE_SPACE,
		(data: {
			payload: {
				groupId: string;
			};
		}) => {
			if (groupID === data.payload.groupId) {
				return;
			}
			createSpaceActions.init();
			setGroupID(data.payload.groupId);
		},
		[groupID]
	);

	sidebarActions.useSidebarEffect(
		SidebarID.CreateSpace,
		(data) => {
			if (!data.isOpen) {
				createSpaceActions.reset();
			}
		},
		[]
	);

	useEventHandler(
		EventID.UPDATE_CREATE_SPACE,
		(data: { meta: string; type: string; payload: Partial<CreateSpaceStateType> }) => {
			switch (data.type) {
				case "update": {
					createSpaceActions.update(data.payload);
					break;
				}

				case "append": {
					createSpaceActions.update({
						[data.meta]: [...(state[data.meta] as string[]), ...(data.payload.inviteUserEmails as string[])]
					});
					break;
				}
			}
		},
		[state]
	);
	return null;
}
